import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { Flex, Box, useScreenResolution, Button, Icon } from "dash-ui";
import LogoImg from "assets/images/png/logo.png";
import ProfileDropdown from "flow-components/Header/components/ProfileDropdown/ProfileDropdown";
import { useLogout } from "common/hooks/useLogout/useLogout";
import QuickStartChecklist, {
  useChecklistState,
} from "common/components/QuickStartChecklist";
import { PortalLanguageSwitcher } from "features/switch-portal-language";

const HeaderStyle = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.colors.n30};
  background-color: ${({ theme }) => theme.colors.white};
  position: fixed;
  z-index: 5;
`;

function Logout() {
  const { t } = useTranslation(["common"]);
  const theme = useTheme();
  const [logout] = useLogout();
  return (
    <div className="flex flex-row items-center gap-2 mr-4">
      <Icon name="logout" color={theme.colors.n90} />
      <a href="#" onClick={logout} className="text-slate-600">
        {t("common:labels.signOut")}
      </a>
    </div>
  );
}

function Header({
  withProfileDropdown = true,
  withLogoutButton = false,
  withChecklist = false,
}: {
  withProfileDropdown?: boolean;
  withLogoutButton?: boolean;
  // Opt-in, and off by default on purpose: this same Header also renders on the
  // public share / preview / legal routes (PortfolioPreviewPage,
  // SharePropertyPreviewPage, FeaturedPortfolioPreviewBase, TermConditions...).
  // The checklist gate below is pure localStorage, which survives logout and is
  // not route-scoped, so defaulting to `true` would show a broker the internal
  // Pulse checklist — upgrade prompts included — on top of the client-facing
  // link they just opened to check. Only the signed-in app shell opts in.
  withChecklist?: boolean;
}) {
  const { t } = useTranslation(["pages"]);
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { isComplete, isRevealed } = useChecklistState();
  // Revealed the moment the Properties quick tour finishes (WelcomeAndTour calls
  // reveal() then markComplete("quick-tour")), and on every later visit from the
  // persisted flag. DSH-857 re-enables this: the checklist was hidden behind
  // `false &&` in ccf2f6b03 so tour QA on beta wasn't mixed with the old flat
  // checklist, and it is now page-aware.
  const showChecklist =
    withChecklist && (isComplete("quick-tour") || isRevealed);

  return (
    <Box h={50}>
      <HeaderStyle
        flex={1}
        w="100%"
        h={50}
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex>
          <Box ml={isMobile ? 4 : 19}>
            <a href={`${window.location.origin}`}>
              <img
                src={LogoImg}
                alt={t("pages:authCommon.logoAlt") || ""}
                width={96}
                height={24}
              />
            </a>
          </Box>
        </Flex>
        <Flex alignItems="center">
          <PortalLanguageSwitcher
            className={
              withProfileDropdown || withLogoutButton ? "mr-1" : "mr-4"
            }
          />
          {showChecklist && (
            <Box ml={2}>
              <QuickStartChecklist />
            </Box>
          )}
          {withProfileDropdown && (
            <Box data-tour="profile">
              <ProfileDropdown />
            </Box>
          )}
          {withLogoutButton && !withProfileDropdown && <Logout />}
        </Flex>
      </HeaderStyle>
    </Box>
  );
}

export default Header;
